import React, { useEffect, useMemo, useState } from "react";
import {
  Center,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import TourModeActionButton from "./TourModeActionButton";

const getRolesInputValue = (roles) => {
  if (Array.isArray(roles)) {
    return roles.join(", ");
  }

  if (typeof roles === "string") {
    return roles;
  }

  return "";
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const buildInitialForm = (survey) => ({
  start_date: toDateInputValue(survey?.start_date),
  end_date: toDateInputValue(survey?.end_date),
  roles: getRolesInputValue(survey?.roles),
  opening_message: survey?.opening_message || "",
  closing_message: survey?.closing_message || "",
  passcode: survey?.passcode || "",
});

const OrganizationSurveyConfigModal = ({
  isOpen,
  onClose,
  onSave,
  survey,
  surveyTitle,
  isLoadingSurvey = false,
  isSubmitting = false,
  isTour = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(buildInitialForm(survey));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen || isLoadingSurvey) return;
    setFormData(buildInitialForm(survey));
    setErrors({});
  }, [isOpen, isLoadingSurvey, survey]);

  const surveyName = useMemo(
    () => surveyTitle || t("organization.survey", "Survey"),
    [surveyTitle, t]
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));

    if (errors[field] || errors.dates) {
      setErrors((current) => ({
        ...current,
        [field]: "",
        dates: "",
      }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      nextErrors.dates = t(
        "organization.surveys_date_validation",
        "Start date must be on or before the end date."
      );
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitting || isLoadingSurvey) return;
    if (!validate()) return;

    try {
      const roles = formData.roles
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean);

      await onSave({
        start_date: formData.start_date || "",
        end_date: formData.end_date || "",
        roles,
        opening_message: formData.opening_message,
        closing_message: formData.closing_message,
        passcode: formData.passcode,
      });
    } catch (err) {
      console.error("Submit failed", err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={isSubmitting || isLoadingSurvey ? () => {} : onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("organization.configure_survey", "Survey Configuration")}
        </ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting || isLoadingSurvey} />
        <ModalBody>
          {isLoadingSurvey ? (
            <Center py={12} flexDirection="column" gap={4}>
              <Heading size="md">{surveyName}</Heading>
              <Text color="gray.600">
                {t("organization.surveys_config_subtitle", "Configure survey settings")}
              </Text>
              <Spinner size="lg" />
              <Text color="gray.600">
                {t("organization.surveys_loading_config", "Loading survey configuration...")}
              </Text>
            </Center>
          ) : (
            <VStack spacing={4} align="stretch">
              <Heading size="md">{surveyName}</Heading>
              <Text color="gray.600">
                {t("organization.surveys_config_subtitle", "Configure survey settings")}
              </Text>

              <FormControl isInvalid={Boolean(errors.dates)}>
                <FormLabel htmlFor="organization-survey-start-date">
                  {t("organization.surveys_start_date", "Start Date")}
                </FormLabel>
                <Input
                  id="organization-survey-start-date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleChange("start_date")}
                />
              </FormControl>

              <FormControl isInvalid={Boolean(errors.dates)}>
                <FormLabel htmlFor="organization-survey-end-date">
                  {t("organization.surveys_end_date", "End Date")}
                </FormLabel>
                <Input
                  id="organization-survey-end-date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange("end_date")}
                />
                {errors.dates ? <FormErrorMessage>{errors.dates}</FormErrorMessage> : null}
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="organization-survey-roles">
                  {t("organization.surveys_roles", "Roles (Teams)")}
                </FormLabel>
                <Input
                  id="organization-survey-roles"
                  value={formData.roles}
                  onChange={handleChange("roles")}
                  placeholder={t(
                    "organization.surveys_roles_placeholder",
                    "Enter roles separated by commas"
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="organization-survey-opening-message">
                  {t("organization.surveys_opening_message", "Opening Message")}
                </FormLabel>
                <Textarea
                  id="organization-survey-opening-message"
                  value={formData.opening_message}
                  onChange={handleChange("opening_message")}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="organization-survey-closing-message">
                  {t("organization.surveys_closing_message", "Closing Message")}
                </FormLabel>
                <Textarea
                  id="organization-survey-closing-message"
                  value={formData.closing_message}
                  onChange={handleChange("closing_message")}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="organization-survey-passcode">
                  {t("organization.surveys_passcode", "Passcode")}
                </FormLabel>
                <Input
                  id="organization-survey-passcode"
                  value={formData.passcode}
                  onChange={handleChange("passcode")}
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting || isLoadingSurvey}>
            {t("common.cancel", "Cancel")}
          </Button>
          <TourModeActionButton
            colorScheme="blue"
            onClick={handleSubmit}
            isTour={isTour}
            isLoading={isSubmitting}
            isDisabled={isLoadingSurvey}
          >
            {t("common.save", "Save")}
          </TourModeActionButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OrganizationSurveyConfigModal;
